/**
 * Utility functions for race tracking replay
 */

/**
 * Calculate bearing between two coordinates
 * @param {number} lat1 - Starting latitude
 * @param {number} lng1 - Starting longitude
 * @param {number} lat2 - Ending latitude
 * @param {number} lng2 - Ending longitude
 * @returns {number} - Bearing in degrees (0-360)
 */
export function calculateBearing(lat1, lng1, lat2, lng2) {
    const toRad = deg => (deg * Math.PI) / 180;
    const toDeg = rad => (rad * 180) / Math.PI;

    const dLng = toRad(lng2 - lng1);
    const lat1Rad = toRad(lat1);
    const lat2Rad = toRad(lat2);

    const y = Math.sin(dLng) * Math.cos(lat2Rad);
    const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
              Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);

    let bearing = toDeg(Math.atan2(y, x));
    bearing = (bearing + 360) % 360;

    return bearing;
}

/**
 * Convert timestamp to epoch milliseconds (handles strings, Date objects, and numbers)
 */
function toEpochMs(timestamp) {
    if (typeof timestamp === 'number') return timestamp;
    if (typeof timestamp === 'string' || timestamp instanceof Date) {
        return new Date(timestamp).getTime();
    }
    return timestamp;
}

/**
 * Interpolate position between two GPS points
 * @param {Array} point1 - [lat, lng, timestamp, speed, battery]
 * @param {Array} point2 - [lat, lng, timestamp, speed, battery]
 * @param {number} targetTime - Target timestamp (epoch ms)
 * @returns {Object} - { lat, lng, speed, bearing, battery }
 */
export function interpolatePosition(point1, point2, targetTime) {
    const t1 = toEpochMs(point1[2]);
    const t2 = toEpochMs(point2[2]);

    // Calculate interpolation factor (0 to 1)
    const t = t2 !== t1 ? (targetTime - t1) / (t2 - t1) : 0;
    const clampedT = Math.max(0, Math.min(1, t));

    // Interpolate coordinates
    const lat = point1[0] + (point2[0] - point1[0]) * clampedT;
    const lng = point1[1] + (point2[1] - point1[1]) * clampedT;

    // Interpolate speed
    const speed = point1[3] + (point2[3] - point1[3]) * clampedT;

    // Calculate bearing from point1 to point2
    const bearing = calculateBearing(point1[0], point1[1], point2[0], point2[1]);

    // Use point1's battery (battery doesn't interpolate smoothly)
    const battery = point1[4];

    return {
        lat,
        lng,
        speed,
        bearing,
        battery,
        timestamp: targetTime,
    };
}

/**
 * Binary search to find the index of the point just before or at targetTime
 * @param {Array} points - Array of GPS points sorted by timestamp
 * @param {number} targetTime - Target timestamp (epoch ms)
 * @returns {number} - Index of point just before targetTime, or -1 if before all points
 */
export function findPointIndexBefore(points, targetTime) {
    if (!points || points.length === 0) return -1;
    const firstTime = toEpochMs(points[0][2]);
    const lastTime = toEpochMs(points[points.length - 1][2]);
    if (targetTime < firstTime) return -1;
    if (targetTime >= lastTime) return points.length - 1;

    let low = 0;
    let high = points.length - 1;

    while (low < high) {
        const mid = Math.floor((low + high + 1) / 2);
        if (toEpochMs(points[mid][2]) <= targetTime) {
            low = mid;
        } else {
            high = mid - 1;
        }
    }

    return low;
}

/**
 * Get interpolated position for a tracker at a given time
 * @param {Array} points - Array of GPS points sorted by timestamp
 * @param {number} targetTime - Target timestamp (epoch ms)
 * @returns {Object|null} - Interpolated position or null if time is outside range
 */
export function getPositionAtTime(points, targetTime) {
    if (!points || points.length === 0) return null;

    const firstTime = toEpochMs(points[0][2]);
    const lastTime = toEpochMs(points[points.length - 1][2]);

    // Before first point - return null (contestant hasn't started yet)
    if (targetTime < firstTime) return null;

    // At or after last point - return last known position
    if (targetTime >= lastTime) {
        const lastPoint = points[points.length - 1];
        const prevPoint = points.length > 1 ? points[points.length - 2] : lastPoint;
        return {
            lat: lastPoint[0],
            lng: lastPoint[1],
            speed: lastPoint[3],
            bearing: calculateBearing(prevPoint[0], prevPoint[1], lastPoint[0], lastPoint[1]),
            battery: lastPoint[4],
            timestamp: toEpochMs(lastPoint[2]),
        };
    }

    // Find surrounding points using binary search
    const idx = findPointIndexBefore(points, targetTime);
    if (idx < 0) return null;

    const point1 = points[idx];
    const point2 = points[idx + 1] || point1;

    return interpolatePosition(point1, point2, targetTime);
}

/**
 * Get all points up to a given time (for trail building)
 * @param {Array} points - Array of GPS points sorted by timestamp
 * @param {number} targetTime - Target timestamp (epoch ms)
 * @param {number} maxPoints - Maximum number of points to return (for performance)
 * @returns {Array} - Filtered array of points with timestamp <= targetTime
 */
export function getPointsUpToTime(points, targetTime, maxPoints = 100) {
    if (!points || points.length === 0) return [];

    const idx = findPointIndexBefore(points, targetTime);
    if (idx < 0) return [];

    // Get points up to and including the found index
    const endIdx = idx + 1;
    const startIdx = Math.max(0, endIdx - maxPoints);

    return points.slice(startIdx, endIdx);
}

/**
 * Calculate race time bounds from all trackers
 * @param {Array} trackers - Array of tracker objects with points arrays
 * @returns {Object} - { startTime, endTime } in epoch ms
 */
export function calculateRaceTimeBounds(trackers) {
    if (!trackers || trackers.length === 0) {
        return { startTime: null, endTime: null };
    }

    let startTime = Infinity;
    let endTime = -Infinity;

    trackers.forEach(tracker => {
        if (tracker.points && tracker.points.length > 0) {
            // Convert timestamp to epoch ms (handles both Date objects and ISO strings)
            const firstRaw = tracker.points[0][2];
            const lastRaw = tracker.points[tracker.points.length - 1][2];
            const first = typeof firstRaw === 'string' || firstRaw instanceof Date
                ? new Date(firstRaw).getTime()
                : firstRaw;
            const last = typeof lastRaw === 'string' || lastRaw instanceof Date
                ? new Date(lastRaw).getTime()
                : lastRaw;

            if (first < startTime) startTime = first;
            if (last > endTime) endTime = last;
        }
    });

    if (startTime === Infinity || endTime === -Infinity) {
        return { startTime: null, endTime: null };
    }

    return { startTime, endTime };
}

/**
 * Calculate distance between two GPS coordinates using Haversine formula
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @returns {number} Distance in meters
 */
export function haversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Calculate distance traveled from GPS points up to target time
 * Uses actual GPS distance, not point index - so mushers resting at checkpoints stay in place
 *
 * @param {Array} points - Array of GPS points [lat, lng, timestamp, speed, battery, elevation]
 * @param {number} targetTime - Target timestamp (epoch ms)
 * @param {Object} interpolatedPosition - The interpolated position at targetTime { lat, lng }
 * @param {number} totalFinalDistance - The final totalDistanceOnTrack from backend
 * @returns {number} - Distance on track in meters
 */
export function calculateDistanceAtTime(points, targetTime, interpolatedPosition, totalFinalDistance) {
    if (!points || points.length === 0) return 0;

    const idx = findPointIndexBefore(points, targetTime);
    if (idx < 0) return 0;

    // Calculate actual GPS distance traveled up to current point
    let distanceTraveled = 0;
    for (let i = 1; i <= idx; i++) {
        const prevPoint = points[i - 1];
        const currPoint = points[i];
        distanceTraveled += haversineDistance(prevPoint[0], prevPoint[1], currPoint[0], currPoint[1]);
    }

    // Add fractional distance to next point if interpolating
    if (idx + 1 < points.length && interpolatedPosition) {
        const currentPoint = points[idx];
        const nextPoint = points[idx + 1];

        // Calculate time-based interpolation factor
        const lastPointTime = toEpochMs(currentPoint[2]);
        const nextPointTime = toEpochMs(nextPoint[2]);

        if (nextPointTime > lastPointTime && targetTime > lastPointTime) {
            const timeFraction = Math.min(1, (targetTime - lastPointTime) / (nextPointTime - lastPointTime));
            const segmentDistance = haversineDistance(currentPoint[0], currentPoint[1], nextPoint[0], nextPoint[1]);
            distanceTraveled += segmentDistance * timeFraction;
        }
    }

    // Calculate total GPS distance for scaling
    let totalGPSDistance = 0;
    for (let i = 1; i < points.length; i++) {
        const prevPoint = points[i - 1];
        const currPoint = points[i];
        totalGPSDistance += haversineDistance(prevPoint[0], prevPoint[1], currPoint[0], currPoint[1]);
    }

    // If we have backend's total distance, scale GPS distance to it
    if (totalFinalDistance && totalFinalDistance > 0 && totalGPSDistance > 0) {
        const scaleFactor = totalFinalDistance / totalGPSDistance;
        return Math.min(distanceTraveled * scaleFactor, totalFinalDistance);
    }

    return distanceTraveled;
}

/**
 * Find the closest point on the elevation profile to a given lat/lng
 * This ensures tracker positions on the graph match their actual map position
 * @param {Array} profile - Elevation profile with {lat, lng, distance, elevation} points
 * @param {number} lat - Tracker's latitude
 * @param {number} lng - Tracker's longitude
 * @returns {Object|null} - Closest profile point or null if no valid match
 */
export function findClosestProfilePoint(profile, lat, lng) {
    if (!profile || profile.length === 0 || lat == null || lng == null) return null;

    let closestPoint = null;
    let closestDistance = Infinity;

    for (const point of profile) {
        if (point.lat == null || point.lng == null) continue;

        const dist = haversineDistance(lat, lng, point.lat, point.lng);
        if (dist < closestDistance) {
            closestDistance = dist;
            closestPoint = point;
        }
    }

    // Only return if within reasonable distance (500m) to avoid false matches
    if (closestPoint && closestDistance < 500) {
        return {
            ...closestPoint,
            matchDistance: closestDistance, // Distance from tracker to profile point
        };
    }

    return null;
}

/**
 * Format milliseconds as HH:MM:SS
 * @param {number} ms - Milliseconds
 * @returns {string} - Formatted time string
 */
export function formatDuration(ms) {
    if (ms == null || isNaN(ms)) return '00:00:00';

    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = n => String(n).padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export default {
    calculateBearing,
    interpolatePosition,
    findPointIndexBefore,
    getPositionAtTime,
    getPointsUpToTime,
    calculateRaceTimeBounds,
    haversineDistance,
    calculateDistanceAtTime,
    findClosestProfilePoint,
    formatDuration,
};
